// src/features/tuition/components/finances/PaymentForm.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box
} from '@mui/material';
import { THEME_COLORS } from '../../../../styles/theme';
import { fetchStudents } from '../../services/studentsService';

const PaymentForm = ({ open, onClose, onSubmit, initialData = null }) => {
  const [students, setStudents] = useState([]);
  const [formData, setFormData] = useState({
    studentId: '',
    studentName: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    method: 'bank transfer',
    status: 'paid',
    description: ''
  });
  
  useEffect(() => {
    // Fetch students for dropdown
    const loadStudents = async () => {
      try {
        const studentsData = await fetchStudents();
        setStudents(studentsData);
      } catch (error) {
        console.error("Error loading students:", error);
        
        // For demo purposes, add mock data
        setStudents([
          { id: '1', firstName: 'Emma', lastName: 'Johnson' },
          { id: '2', firstName: 'Liam', lastName: 'Smith' },
          { id: '3', firstName: 'Sophia', lastName: 'Williams' }
        ]);
      }
    };
    
    loadStudents();
  }, []);
  
  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        // Ensure all expected fields exist
        description: initialData.description || ''
      });
    } else {
      resetForm();
    }
  }, [initialData, open]);
  
  const resetForm = () => {
    setFormData({
      studentId: '',
      studentName: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      method: 'bank transfer',
      status: 'paid',
      description: ''
    });
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // If student is selected, update the student name
    if (name === 'studentId') {
      const selectedStudent = students.find(s => s.id === value);
      if (selectedStudent) {
        setFormData(prev => ({
          ...prev,
          studentId: value,
          studentName: `${selectedStudent.firstName} ${selectedStudent.lastName}`
        }));
      }
    }
  };
  
  const handleSubmit = () => {
    // Basic validation
    if (!formData.studentId || !formData.amount || !formData.date) {
      alert('Please fill in all required fields');
      return;
    }
    
    onSubmit({
      ...formData,
      amount: parseFloat(formData.amount)
    });
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          {initialData ? 'Edit Payment' : 'Add New Payment'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ p: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Student</InputLabel>
                <Select
                  name="studentId"
                  value={formData.studentId}
                  label="Student"
                  onChange={handleInputChange}
                >
                  <MenuItem value="">
                    <em>Select a student</em>
                  </MenuItem>
                  {students.map(student => (
                    <MenuItem key={student.id} value={student.id}>
                      {student.firstName} {student.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Amount"
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleInputChange}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>R</Typography>
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Date"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  name="method"
                  value={formData.method}
                  label="Payment Method"
                  onChange={handleInputChange}
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="bank transfer">Bank Transfer</MenuItem>
                  <MenuItem value="credit card">Credit Card</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  label="Status"
                  onChange={handleInputChange}
                >
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                multiline
                rows={2}
                value={formData.description}
                onChange={handleInputChange}
                placeholder="e.g., Monthly tuition fee, Study materials, etc."
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          sx={{
            bgcolor: THEME_COLORS.primary,
            '&:hover': {
              bgcolor: '#1e1c4e'
            }
          }}
        >
          {initialData ? 'Update Payment' : 'Add Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentForm;